import { AxiosError } from 'axios'
import isEmpty from 'lodash/isEmpty'
import { action, computed, flow, makeAutoObservable, observable } from 'mobx'
import { computedFn } from 'mobx-utils/lib/computedFn'

import { requestWrapper, saveFile } from 'Shared/utils'

import { STORE_UI } from 'Core/constants'
import { CustomGenerator, TransformResponseType } from 'Core/interfaces'
import {
	entityToLabelValue,
	entityToStringLabelValue,
	getDefaultStoreArrayValue,
	getDefaultStoreValue
} from 'Core/utils'

import { getAllOptions, STORE_SERVICE_LOCATION } from 'Portals/Customer/constants'
import { PurchaseOrder } from 'Portals/Customer/entities'
import { ServiceSite } from 'Portals/Customer/Interfaces'
import { formatBillingLocationAddressOption } from 'Portals/Customer/Pages/PurchaseOrder/Generic/utils/helpers'
import { PurchaseOrderRepo, ServiceSiteRepo } from 'Portals/Customer/repositories'
import { RootStore } from 'Portals/Customer/stores'
import i18n from 'Portals/Customer/utils/i18nService'

import { PurchaseOrderTable } from './PurchaseOrderTable'

export class PurchaseOrderStore {
	table: PurchaseOrderTable
	@observable totalBalance = getDefaultStoreValue<number>()
	@observable statuses = getDefaultStoreArrayValue<Entity>()
	@observable billingLocationAddresses =
		getDefaultStoreArrayValue<PurchaseOrder.BillingLocationAddress>()
	@observable serviceSites = getDefaultStoreArrayValue<ServiceSite>()
	@observable purchaseOrder = getDefaultStoreValue<PurchaseOrder.Detailed>()
	@observable purchaseOrderHistory = getDefaultStoreArrayValue<PurchaseOrder.History>()
	@observable isDocumentLoading = false

	constructor(private rootStore: RootStore) {
		this.table = new PurchaseOrderTable(rootStore)
		makeAutoObservable(this, {}, { autoBind: true })
	}

	@computed
	get isFiltersOptionsLoading() {
		return (
			this.totalBalance.isLoading ||
			this.statuses.isLoading ||
			this.billingLocationAddresses.isLoading ||
			this.rootStore[STORE_SERVICE_LOCATION].billingLocation.isLoading
		)
	}

	@computed
	get statusOptions() {
		return [getAllOptions(), ...this.statuses.value.map(entityToStringLabelValue)]
	}

	@computed
	get totalCurrencyCode() {
		return (
			this.billingLocationAddresses.value[0]?.customerCurrencyCode ??
			i18n.t('pages:purchaseOrders.defaultCurrencyCode')
		)
	}

	@computed
	get billingLocationAddressOptions() {
		return this.billingLocationAddresses.value.map(formatBillingLocationAddressOption)
	}

	@flow
	*retrieveServiceSites(): CustomGenerator<ServiceSite[]> {
		yield requestWrapper({
			field: this.serviceSites,
			repository: ServiceSiteRepo.retrieveServiceSites,
			repositoryParams: null
		})
	}

	@computed
	get serviceSiteOptions() {
		return this.serviceSites.value.map(entityToLabelValue)
	}

	serviceSiteOptionsById = computedFn((customerId: BasicNumber): LabelValue<number>[] =>
		this.serviceSites.value
			?.filter((serviceSite) => serviceSite.customerId === customerId)
			?.map((option) => entityToLabelValue(option))
	);

	@flow
	*retrieveTotalBalance(): CustomGenerator<number> {
		yield requestWrapper({
			field: this.totalBalance,
			repository: PurchaseOrderRepo.retrievePurchaseOrderBalance,
			repositoryParams: null
		})
	}

	@flow
	*retrievePurchaseOrderStatuses(): CustomGenerator<Entity[]> {
		yield requestWrapper({
			field: this.statuses,
			repository: PurchaseOrderRepo.retrievePurchaseOrderStatuses,
			repositoryParams: null
		})
	}

	@flow
	*retrieveBillingLocationAddresses(): CustomGenerator<PurchaseOrder.BillingLocationAddress[]> {
		yield requestWrapper({
			field: this.billingLocationAddresses,
			repository: PurchaseOrderRepo.retrieveBillingLocationAddresses,
			repositoryParams: null
		})
	}

	@flow
	*retrievePurchaseOrder(purchaseOrderId: Key): CustomGenerator<PurchaseOrder.Detailed> {
		yield requestWrapper({
			field: this.purchaseOrder,
			repository: PurchaseOrderRepo.retrievePurchaseOrder,
			repositoryParams: purchaseOrderId,
			showDefaultError: false,
			errorCallback: (error) => {
				this.rootStore[STORE_UI].showErrorNotification(error)
			}
		})
	}

	@flow
	*retrievePurchaseOrderHistory(purchaseOrderId: Key): CustomGenerator<PurchaseOrder.History[]> {
		yield requestWrapper({
			field: this.purchaseOrderHistory,
			repository: PurchaseOrderRepo.retrievePurchaseOrderHistory,
			repositoryParams: purchaseOrderId,
			showDefaultError: false,
			errorCallback: (error) => {
				this.rootStore[STORE_UI].showErrorNotification(error)
			}
		})
	}

	@action
	resetPurchaseOrder() {
		this.purchaseOrder = getDefaultStoreValue<PurchaseOrder.Detailed>()
		this.purchaseOrderHistory = getDefaultStoreArrayValue<PurchaseOrder.History>()
	}

	@computed
	get isEditFormLoading() {
		return this.purchaseOrder.isLoading || this.purchaseOrderHistory.isLoading
	}

	@computed
	get orderWithHistory() {
		return !isEmpty(this.purchaseOrderHistory.value)
	}

	@action
	clearTotalBalance() {
		this.totalBalance = getDefaultStoreValue<number>()
	}

	@flow
	*savePurchaseOrder(
		payload: PurchaseOrder.Request.Generic | PurchaseOrder.Request.WithHistory,
		purchaseOrderId?: Key
	): CustomGenerator<PurchaseOrder.Detailed> {
		this.rootStore[STORE_UI].setIsRequestSubmitting()

		try {
			if (purchaseOrderId) {
				this.orderWithHistory
					? yield PurchaseOrderRepo.editPurchaseOrderWithHistory(
							purchaseOrderId,
							payload as PurchaseOrder.Request.WithHistory
					  )
					: yield PurchaseOrderRepo.editPurchaseOrder(
							purchaseOrderId,
							payload as PurchaseOrder.Request.Generic
					  )
			} else {
				yield PurchaseOrderRepo.addPurchaseOrder(payload as PurchaseOrder.Request.Generic)
			}

			this.table.getItems()
			this.rootStore[STORE_UI].closeModal()
			this.rootStore[STORE_UI].showSuccessNotification(
				i18n.t('pages:purchaseOrders.messages.successSave')
			)
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		} finally {
			this.rootStore[STORE_UI].setIsRequestSubmitting(false)
		}
	}

	getCurrencyCodeByCustomerId = computedFn(
		(customerId: BasicNumber): BasicString =>
			this.billingLocationAddresses.value?.find(
				(address) => address.customerId === customerId
			)?.customerCurrencyCode || null
	);

	@flow
	*downloadPurchaseOrderFile(
		purchaseOrderId: Key,
		fileName?: string
	): CustomGenerator<TransformResponseType> {
		if (!purchaseOrderId) {
			return
		}

		try {
			this.isDocumentLoading = true
			const document = yield PurchaseOrderRepo.downloadPurchaseOrderFile(purchaseOrderId)

			const { data, headers } = document
			saveFile({ data, headers, fileName })
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		} finally {
			this.isDocumentLoading = false
		}
	}

	@flow
	*downloadPurchaseOrderHistoryFile(
		purchaseOrderId: Key,
		fileId?: Key,
		fileName?: string
	): CustomGenerator<TransformResponseType> {
		if (!purchaseOrderId || !fileId) {
			return
		}

		try {
			this.isDocumentLoading = true
			const document = yield PurchaseOrderRepo.downloadPurchaseOrderHistoryFile(
				purchaseOrderId,
				fileId
			)

			const { data, headers } = document
			saveFile({ data, headers, fileName })
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		} finally {
			this.isDocumentLoading = false
		}
	}
}
